import apiClient from "../../../shared/services/apiClient";
import {
  Perspective,
  PerspectivePreset,
  VersionSnapshot,
} from "../types/perspective.types";

const PERSPECTIVE_API_BASE = "/perspectives";

function unwrapPayload<T>(payload: unknown): T | null {
  if (payload && typeof payload === "object" && "data" in (payload as any)) {
    return ((payload as any).data as T) ?? null;
  }
  return (payload as T) ?? null;
}

function coerceNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
}

function coerceBoolean(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    if (value.toLowerCase() === "true") return true;
    if (value.toLowerCase() === "false") return false;
  }
  return undefined;
}

function normalizeNotchFilter(value: unknown): number | undefined {
  const numeric = coerceNumber(value);
  if (numeric !== undefined) {
    return numeric;
  }
  const bool = coerceBoolean(value);
  if (bool === true) {
    return 60;
  }
  return undefined;
}

export function normalizePerspective(raw: any): Perspective {
  const source = raw ?? {};
  const displaySource =
    source.display_config || source.display_options || source.displayConfig || {};
  const filterSource = source.filter_config || source.filters || {};
  const timeWindowSource =
    source.time_window_config || source.timeWindowConfig || {};
  const waveformSource =
    source.waveform_settings || source.waveformSettings || {};
  const clinicalDefaultsSource =
    source.clinical_defaults ||
    source.clinicalDefaults ||
    source.eeg_defaults ||
    source.eegDefaults ||
    {};

  return {
    perspective_id: source.perspective_id || source.id || source.uuid,
    perspective_name: source.perspective_name || source.name || source.title || "",
    description: source.description || source.summary || "",
    display_config: {
      layout:
        displaySource.layout === "grid" ||
        displaySource.layout === "cascade" ||
        displaySource.layout === "stacked"
          ? displaySource.layout
          : "stacked",
      columns: coerceNumber(displaySource.columns),
      rowHeight:
        coerceNumber(displaySource.rowHeight) ??
        coerceNumber(displaySource.channelHeight) ??
        50,
      channelSpacing: coerceNumber(displaySource.channelSpacing) ?? 10,
    },
    filter_config: {
      highPassFilter:
        coerceNumber(
          filterSource.highPassFilter ??
            filterSource.highpass ??
            filterSource.high_pass_filter,
        ) ?? undefined,
      lowPassFilter:
        coerceNumber(
          filterSource.lowPassFilter ??
            filterSource.lowpass ??
            filterSource.low_pass_filter,
        ) ?? undefined,
      notchFilter: normalizeNotchFilter(
        filterSource.notchFilter ??
          filterSource.notch ??
          filterSource.notch_filter_60hz,
      ),
      amplitudeMin: coerceNumber(
        filterSource.amplitudeMin ?? filterSource.amplitude_min,
      ),
      amplitudeMax: coerceNumber(
        filterSource.amplitudeMax ?? filterSource.amplitude_max,
      ),
    },
    time_window_config: {
      duration:
        coerceNumber(timeWindowSource.duration) ??
        coerceNumber(displaySource.timeScale) ??
        10,
      overlap: coerceNumber(timeWindowSource.overlap),
      scrollSpeed:
        coerceNumber(timeWindowSource.scrollSpeed) ??
        coerceNumber(timeWindowSource.scroll_speed),
    },
    waveform_settings: {
      lineWidth:
        coerceNumber(waveformSource.lineWidth ?? waveformSource.line_width) ?? 1,
      colorScheme:
        waveformSource.colorScheme ??
        displaySource.colorScheme ??
        "default",
      customColors:
        waveformSource.customColors ?? waveformSource.custom_colors ?? undefined,
      showGrid:
        coerceBoolean(waveformSource.showGrid ?? displaySource.showGrid) ?? true,
      showAnnotations:
        coerceBoolean(
          waveformSource.showAnnotations ?? waveformSource.show_annotations,
        ) ?? true,
      showEvents:
        coerceBoolean(waveformSource.showEvents ?? waveformSource.show_events) ??
        true,
    },
    channel_visibility: source.channel_visibility || source.channelVisibility || [],
    montage_id: source.montage_id || source.montageId,
    event_set_id:
      source.event_set_id ||
      source.eventSetId ||
      clinicalDefaultsSource.eventSetId ||
      clinicalDefaultsSource.event_set_id,
    trend_template_id:
      source.trend_template_id ||
      source.trendTemplateId ||
      clinicalDefaultsSource.trendTemplateId ||
      clinicalDefaultsSource.trend_template_id,
    label_system_id:
      source.label_system_id ||
      source.labelSystemId ||
      clinicalDefaultsSource.labelSystemId ||
      clinicalDefaultsSource.label_system_id,
    clinical_defaults: {
      eventSetId:
        source.event_set_id ||
        source.eventSetId ||
        clinicalDefaultsSource.eventSetId ||
        clinicalDefaultsSource.event_set_id,
      trendTemplateId:
        source.trend_template_id ||
        source.trendTemplateId ||
        clinicalDefaultsSource.trendTemplateId ||
        clinicalDefaultsSource.trend_template_id,
      labelSystemId:
        source.label_system_id ||
        source.labelSystemId ||
        clinicalDefaultsSource.labelSystemId ||
        clinicalDefaultsSource.label_system_id,
      sensitivityUvPerCm:
        coerceNumber(
          clinicalDefaultsSource.sensitivityUvPerCm ??
            clinicalDefaultsSource.sensitivity_uv_per_cm ??
            source.sensitivity_uv_per_cm,
        ) ?? 70,
      deflectionUv:
        coerceNumber(
          clinicalDefaultsSource.deflectionUv ??
            clinicalDefaultsSource.deflection_uv ??
            source.deflection_uv,
        ) ?? 100,
      timelinePosition:
        clinicalDefaultsSource.timelinePosition === "top" ||
        clinicalDefaultsSource.timeline_position === "top"
          ? "top"
          : "bottom",
    },
    is_default: source.is_default || source.isDefault || false,
    is_system: source.is_system || source.isSystem || false,
    view_count: coerceNumber(source.view_count ?? source.views) || 0,
    created_by: source.created_by || source.createdBy || null,
    is_shared: source.is_shared || source.isShared || false,
    is_active:
      source.is_active !== undefined
        ? source.is_active
        : source.active !== undefined
          ? source.active
          : true,
    created_at: source.created_date || source.created_at || source.createdAt,
    updated_at: source.updated_date || source.updated_at || source.updatedAt,
    // Phase B fields
    thumbnail_data: source.thumbnail_data || source.thumbnailData,
    schema_version: source.schema_version || source.schemaVersion || "2.0",
    duplicated_from: source.duplicated_from || source.duplicatedFrom,
    version_history: source.version_history || source.versionHistory || [],
  } as Perspective;
}

export const fetchPerspectives = async (): Promise<Perspective[]> => {
  const response = await apiClient.get<{
    success: boolean;
    data: Perspective[];
  }>(PERSPECTIVE_API_BASE);
  const payload = unwrapPayload<unknown[]>(response.data);
  const raw = Array.isArray(payload) ? payload : [];
  return raw.map(normalizePerspective);
};

export const fetchPerspectiveById = async (
  id: string,
): Promise<Perspective> => {
  const response = await apiClient.get<{ success: boolean; data: Perspective }>(
    `${PERSPECTIVE_API_BASE}/${id}`,
  );
  const raw = unwrapPayload(response.data);
  return normalizePerspective(raw);
};

export const createPerspective = async (
  perspective: Partial<Perspective>,
): Promise<Perspective> => {
  // Add version snapshot for creation
  const versionSnapshot: VersionSnapshot = {
    version: perspective.schema_version || "2.0",
    timestamp: new Date().toISOString(),
    changes_summary: "Initial creation",
    created_by: perspective.created_by,
  };

  const perspectiveWithVersion = {
    ...perspective,
    schema_version: "2.0",
    version_history: [versionSnapshot],
  };

  const response = await apiClient.post<{
    success: boolean;
    data: Perspective;
  }>(PERSPECTIVE_API_BASE, perspectiveWithVersion);
  return normalizePerspective(unwrapPayload(response.data));
};

export const updatePerspective = async (
  id: string,
  perspective: Partial<Perspective>,
): Promise<Perspective> => {
  // Fetch existing perspective to get version history
  const existing = await fetchPerspectiveById(id);

  // Create new version snapshot
  const versionSnapshot: VersionSnapshot = {
    version: perspective.schema_version || existing.schema_version || "2.0",
    timestamp: new Date().toISOString(),
    changes_summary: "Updated configuration",
    created_by: perspective.created_by,
  };

  // Append to version history (keep last 10)
  const perspectiveWithVersion = {
    ...perspective,
    version_history: [
      ...(existing.version_history || []),
      versionSnapshot,
    ].slice(-10), // Keep last 10 versions
  };

  const response = await apiClient.put<{ success: boolean; data: Perspective }>(
    `${PERSPECTIVE_API_BASE}/${id}`,
    perspectiveWithVersion,
  );
  return normalizePerspective(unwrapPayload(response.data));
};

export const deletePerspective = async (id: string): Promise<void> => {
  await apiClient.delete(`${PERSPECTIVE_API_BASE}/${id}`);
};

/**
 * Share a perspective with specified scope
 * @param id - Perspective ID to share
 * @param shareData - Sharing configuration (scope and recipients)
 */
export const sharePerspective = async (
  id: string,
  shareData: {
    scope: "private" | "team" | "system";
    recipients?: string[];
  },
): Promise<void> => {
  await apiClient.post(`${PERSPECTIVE_API_BASE}/${id}/share`, shareData);
};

export const getPerspectivePresets = (): PerspectivePreset[] => {
  return [
    {
      id: "standard-10s",
      name: "Standard 10-Second View",
      description:
        "10-second window with standard filtering for routine review",
      perspective: {
        display_config: {
          layout: "stacked",
          rowHeight: 50,
          channelSpacing: 10,
        },
        filter_config: {
          highPassFilter: 0.5,
          lowPassFilter: 70,
          notchFilter: 60,
        },
        time_window_config: { duration: 10, scrollSpeed: 30 },
        waveform_settings: {
          lineWidth: 1,
          colorScheme: "default",
          showGrid: true,
          showAnnotations: true,
          showEvents: true,
        },
      },
    },
    {
      id: "compressed-30s",
      name: "Compressed 30-Second View",
      description: "Longer time window for pattern recognition and trending",
      perspective: {
        display_config: { layout: "stacked", rowHeight: 40, channelSpacing: 5 },
        filter_config: {
          highPassFilter: 0.5,
          lowPassFilter: 70,
          notchFilter: 60,
        },
        time_window_config: { duration: 30, scrollSpeed: 30 },
        waveform_settings: {
          lineWidth: 0.8,
          colorScheme: "default",
          showGrid: true,
          showAnnotations: false,
          showEvents: true,
        },
      },
    },
    {
      id: "seizure-detection",
      name: "Seizure Detection",
      description:
        "Optimized for identifying seizure activity and abnormal patterns",
      perspective: {
        display_config: {
          layout: "stacked",
          rowHeight: 60,
          channelSpacing: 15,
        },
        filter_config: {
          highPassFilter: 1,
          lowPassFilter: 100,
          amplitudeMin: -500,
          amplitudeMax: 500,
        },
        time_window_config: { duration: 10, scrollSpeed: 25 },
        waveform_settings: {
          lineWidth: 1.2,
          colorScheme: "region",
          showGrid: true,
          showAnnotations: true,
          showEvents: true,
        },
      },
    },
    {
      id: "sleep-staging",
      name: "Sleep Staging",
      description: "Optimized for sleep stage identification and scoring",
      perspective: {
        display_config: {
          layout: "stacked",
          rowHeight: 50,
          channelSpacing: 10,
        },
        filter_config: { highPassFilter: 0.3, lowPassFilter: 35 },
        time_window_config: { duration: 30, scrollSpeed: 30 },
        waveform_settings: {
          lineWidth: 1,
          colorScheme: "default",
          showGrid: true,
          showAnnotations: true,
          showEvents: true,
        },
      },
    },
    {
      id: "high-frequency",
      name: "High-Frequency Activity",
      description: "Focus on high-frequency oscillations and fast ripples",
      perspective: {
        display_config: { layout: "stacked", rowHeight: 45, channelSpacing: 8 },
        filter_config: {
          highPassFilter: 80,
          lowPassFilter: 500,
          amplitudeMin: -100,
          amplitudeMax: 100,
        },
        time_window_config: { duration: 5, scrollSpeed: 15 },
        waveform_settings: {
          lineWidth: 0.8,
          colorScheme: "default",
          showGrid: true,
          showAnnotations: true,
          showEvents: true,
        },
      },
    },
    {
      id: "grid-view",
      name: "Grid View (Multi-Channel)",
      description: "Grid layout for simultaneous viewing of multiple channels",
      perspective: {
        display_config: {
          layout: "grid",
          columns: 4,
          rowHeight: 150,
          channelSpacing: 10,
        },
        filter_config: { highPassFilter: 0.5, lowPassFilter: 70 },
        time_window_config: { duration: 10, scrollSpeed: 30 },
        waveform_settings: {
          lineWidth: 1,
          colorScheme: "region",
          showGrid: true,
          showAnnotations: false,
          showEvents: true,
        },
      },
    },
  ];
};
